import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Alert,
  AlertDescription,
  useProductBrand,
  DataPreviewSvg
} from "@easytestdata/ui";
import { AlertCircle, ArrowLeft, Loader2 } from "lucide-react";
import { useAppConfig } from "@/contexts/AppConfigContext";
import { brand } from "../brand";

function oauthUrl(provider: string): string {
  return `/api/v1/auth/${provider}`;
}

/** Messages for `/login?error=<code>` redirects from the server's OAuth callbacks. */
const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  oauth_state_invalid:
    "That sign-in link expired or was started in another browser. Please try again.",
  oauth_failed: "Sign-in with that provider failed. Please try again.",
  oauth_cancelled: "Sign-in was cancelled or declined at the provider. Please try again.",
  oauth_callback_failed: "Sign-in could not be completed. Please try again.",
  oauth_unavailable: "Sign-in is temporarily unavailable. Please try again shortly.",
  oauth_no_email:
    "That provider did not share an email address. Please use a different sign-in method.",
  oauth_email_unverified:
    "That provider has not verified your email address. Verify it with the provider, or use a different sign-in method.",
  account_suspended: "This account is suspended."
};

// Signing in leaves for the provider and comes back to /login?auth_code=..., so an invite
// token or protected page destination is kept for this tab meanwhile.
const PENDING_INVITE_KEY = "easytestdata:pending-invite";
const PENDING_DESTINATION_KEY = "easytestdata:pending-destination";

function readPendingValue(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writePendingValue(key: string, value: string | null) {
  try {
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  } catch {
    // Storage unavailable: the original link can simply be opened again after signing in.
  }
}

function safeDestination(value: string | null): string | null {
  if (!value?.startsWith("/") || value.startsWith("//") || value.startsWith("/\\")) {
    return null;
  }
  try {
    const url = new URL(value, window.location.origin);
    if (
      url.origin !== window.location.origin ||
      url.pathname.startsWith("//") ||
      decodeURIComponent(url.pathname).replace(/\/+$/, "").toLowerCase() === "/login"
    ) {
      return null;
    }
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
}

/** Sign-in is Intuit, Google or GitHub only: the page is those buttons plus the OAuth return. */
export function LoginPage() {
  const { name, repoUrl, supportsSourceReads } = useProductBrand();
  const BrandLogo = brand.logo;
  const { exchangeOAuthCode, user } = useAuth();
  const { enabledOAuthProviders, deployment, marketingUrl } = useAppConfig();
  // The hosted Terms of Service and the marketing site only exist on EasyTestData Cloud.
  const showTerms = deployment !== "local";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const consumedAuthCodeRef = useRef<string | null>(null);
  const inviteTokenRef = useRef<string | null | undefined>(undefined);
  if (inviteTokenRef.current === undefined) {
    inviteTokenRef.current =
      searchParams.get("invite_token") || readPendingValue(PENDING_INVITE_KEY);
  }
  const destination = useMemo(() => {
    if (deployment === "local") return null;
    if (searchParams.has("next")) return safeDestination(searchParams.get("next"));
    // A fresh login visit discards an abandoned destination; OAuth returns keep it for retries.
    if (searchParams.has("auth_code") || searchParams.has("error")) {
      return safeDestination(readPendingValue(PENDING_DESTINATION_KEY));
    }
    return null;
  }, [searchParams, deployment]);

  useEffect(() => {
    const inviteToken = inviteTokenRef.current;
    if (!user) {
      writePendingValue(PENDING_INVITE_KEY, inviteToken ?? null);
      writePendingValue(PENDING_DESTINATION_KEY, destination);
      return;
    }
    writePendingValue(PENDING_INVITE_KEY, null);
    writePendingValue(PENDING_DESTINATION_KEY, null);
    if (inviteToken) {
      navigate(`/invite?token=${encodeURIComponent(inviteToken)}`, { replace: true });
      return;
    }
    navigate(destination || "/home", { replace: true });
  }, [user, navigate, destination]);

  useEffect(() => {
    const code = searchParams.get("error");
    if (!code) return;
    setError(
      OAUTH_ERROR_MESSAGES[code] ||
        (code.endsWith("_oauth_not_configured")
          ? "That sign-in provider is not configured on this server."
          : "Sign-in failed. Please try again.")
    );
  }, [searchParams]);

  useEffect(() => {
    const code = searchParams.get("auth_code");
    if (!code || consumedAuthCodeRef.current === code) return;
    consumedAuthCodeRef.current = code;
    setLoading(true);
    setError("");
    // On success the effect watching `user` navigates (to the invite, if one is pending).
    exchangeOAuthCode(code)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [searchParams, exchangeOAuthCode]);

  const darkPanel = (
    <div className="relative flex flex-col items-center justify-center overflow-hidden bg-[#0c1222] px-8 py-12 lg:py-0">
      {/* Radial glow */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/10 blur-3xl" />

      {showTerms && (
        <a
          href={marketingUrl}
          className="absolute left-6 top-6 z-10 flex items-center gap-1.5 text-sm text-slate-400 transition-colors hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to website
        </a>
      )}

      <div className="relative z-10 flex flex-col items-center gap-6 text-center">
        <BrandLogo className="h-12 w-12" />
        <div>
          <h1 className="text-2xl font-bold text-white">{name}</h1>
          <p className="mt-2 max-w-xs text-sm text-slate-400">{brand.description}</p>
        </div>
        <DataPreviewSvg className="mt-4 w-full max-w-[280px]" />
        <p className="mt-2 max-w-xs text-xs text-slate-500">
          {supportsSourceReads
            ? "Real companies are read only. Generated data goes only to sandboxes."
            : `Sandbox-only: ${name} never connects to a production QuickBooks company.`}
        </p>
      </div>
    </div>
  );

  // Intuit first: most users already have the developer account their sandbox lives in.
  const oauthButtons =
    enabledOAuthProviders.length > 0 ? (
      <div className="grid gap-2">
        {enabledOAuthProviders.includes("intuit") && (
          <Button variant="outline" className="w-full" asChild>
            <a href={oauthUrl("intuit")}>
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="12" fill="#39B54A" />
                <rect x="10.5" y="10" width="3" height="8" rx="1" fill="white" />
                <circle cx="12" cy="7" r="1.75" fill="white" />
              </svg>
              Continue with Intuit
            </a>
          </Button>
        )}
        {enabledOAuthProviders.includes("google") && (
          <Button variant="outline" className="w-full" asChild>
            <a href={oauthUrl("google")}>
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Continue with Google
            </a>
          </Button>
        )}
        {enabledOAuthProviders.includes("github") && (
          <Button variant="outline" className="w-full" asChild>
            <a href={oauthUrl("github")}>
              <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
              </svg>
              Continue with GitHub
            </a>
          </Button>
        )}
      </div>
    ) : (
      <p className="text-center text-sm text-muted-foreground">
        No sign-in providers are configured on this server.
      </p>
    );

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      {darkPanel}
      <div className="flex flex-1 items-center justify-center bg-gradient-to-br from-blue-50 via-slate-50 to-gray-100 p-4">
        <Card className="w-full max-w-md rounded-2xl shadow-xl">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Sign in</CardTitle>
            <CardDescription>New here? Signing in creates your account.</CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Signing you in...
              </div>
            ) : (
              oauthButtons
            )}
            {repoUrl !== null && (
              <p className="mt-6 text-center text-xs text-muted-foreground">
                Free and open source (Apache-2.0).{" "}
                <a
                  href={repoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Read the code on GitHub
                </a>
                .
              </p>
            )}
            {showTerms && (
              <p className="mt-2 text-center text-xs text-muted-foreground">
                By signing in you agree to the{" "}
                <a
                  href={`${marketingUrl}/legal/terms`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline"
                >
                  Terms
                </a>
                .
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
